import React, { useEffect, useState } from 'react'
import SContent from '../helper/SidebarContent/'
import { Form, Input, InputNumber, message, Modal, Table } from 'antd'
import { DeleteOutlined, EditOutlined, HistoryOutlined } from '@ant-design/icons'
import { FormattedMessage, useIntl } from 'react-intl'
import AddExpense from './AddExpense'
import styles from './expense.module.scss'
import API from '../../api'
import { Link } from 'react-router-dom'
import { getDate } from '../helper/helper'
import { hasPermission } from '../../api/user'
import { useLoading } from '../helper/Hooks'
import SearchDropdown from '../helper/searchDropdown/SearchDropdown'
import moment from 'moment'
import { formatNumber } from '../helper/Integer/IntegerFormatter'
import DateRangeFilter from '../helper/DateRangeFilter'

const { confirm } = Modal
const { TextArea } = Input

export default function Expenses() {
  const language = useIntl()
  const [expenses, setExpenses] = useState([])
  const [filter, setFilter] = useState({
    byUser: '',
    fromUser: '',
    type: '',
    start: moment().subtract(3, 'month').valueOf(),
    end: moment().endOf('month').valueOf(),
  })
  const [totals, setTotals] = useState({
    total_clients: 0,
    total_balance: '',
  })
  const { loading, startLoading } = useLoading(true)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [editForm] = Form.useForm()
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const [historyData, setHistoryData] = useState([])

  useEffect(() => {
    getExpenses()
  }, [filter])

  const getExpenses = () => {
    startLoading(async () => {
      const response = await API.get(
        `/expense?user=${filter.byUser}&fromUser=${filter.fromUser}&type=${filter.type}&start=${filter.start}&end=${filter.end}`
      )
      const expenses = response.data.result
      setExpenses(expenses)
      let totalBalance = 0

      expenses.forEach(({ price }) => {
        totalBalance += price
      })

      setTotals({
        total_clients: expenses.length,
        total_balance: totalBalance,
      })
    })
  }

  const archiveExpense = async (expenseId) => {
    confirm({
      title: language.formatMessage({ id: 'expense_archive_question' }),
      async onOk() {
        try {
          await API.put(`/expense/archive/${expenseId}`)
          getExpenses()
        } catch (err) {
          message.error(err.message)
        }
      },
    })
  }

  const onDateChange = ({ start, end }) => {
    setFilter({
      ...filter,
      start: start,
      end: end,
    })
  }

  const openEditModal = (record) => {
    setEditingExpense(record)
    editForm.setFieldsValue({
      price: record.price,
      description: record.description,
    })
    setEditModalVisible(true)
  }

  const handleEditExpense = async () => {
    try {
      const values = await editForm.validateFields()
      setEditLoading(true)
      await API.put(`/expense/${editingExpense._id}`, {
        description: values.description,
        price: values.price,
      })
      message.success(language.formatMessage({ id: 'save' }))
      setEditModalVisible(false)
      setEditingExpense(null)
      editForm.resetFields()
      getExpenses()
    } catch (err) {
      if (err.response && err.response.data) {
        message.error(err.response.data.message || err.message)
      }
    } finally {
      setEditLoading(false)
    }
  }

  return (
    <SContent title={<FormattedMessage id="expenses" />}>
      <div className={styles.expenseContainer}>
        <AddExpense onExpenseAdd={getExpenses} />
        {hasPermission('expense_history_view') && (
          <div className={styles.tableContainer}>
            <div className={styles.filterContainer}>
              <DateRangeFilter
                onChange={onDateChange}
                placeholder={language.formatMessage({ id: 'time' })}
                defaultValue="3"
                style={{ width: 200, marginBottom: 10 }}
                intervals={[3]}
              />
              <SearchDropdown
                placeholder={language.formatMessage({ id: 'type' })}
                width={200}
                url="/expense/type"
                onSelect={(selectedValue) =>
                  setFilter({
                    ...filter,
                    byUser: filter.byUser,
                    fromUser: filter.fromUser,
                    type: selectedValue ? selectedValue : '',
                  })
                }
                renderItem={(item) => ({
                  key: item._id,
                  value: item._id,
                  name: item.name,
                })}
              />
              <SearchDropdown
                placeholder={language.formatMessage({ id: 'name' })}
                style={{ marginBottom: 10 }}
                width={200}
                url="/user/v2?roles=manager,postman,admin,superadmin&blocked=false"
                searchUrl="/user/v2?roles=manager,postman,admin,superadmin&blocked=false&name="
                onSelect={(selectedValue) =>
                  setFilter({
                    ...filter,
                    byUser: selectedValue ? selectedValue : '',
                    fromUser: filter.fromUser,
                    type: filter.type,
                  })
                }
                renderItem={(item) => ({
                  key: item._id,
                  value: item._id,
                  name: `${item.fullname} (${language
                    .formatMessage({
                      id: item.role.name,
                    })
                    .toLocaleLowerCase()})`,
                })}
              />
              <SearchDropdown
                placeholder={language.formatMessage({ id: 'from' })}
                width={200}
                url="/user/v2?roles=manager,postman,admin,superadmin&blocked=false"
                searchUrl="/user/v2?roles=manager,postman,admin,superadmin&blocked=false&name="
                onSelect={(selectedValue) =>
                  setFilter({
                    ...filter,
                    byUser: filter.byUser,
                    fromUser: selectedValue ? selectedValue : '',
                    type: filter.type,
                  })
                }
                renderItem={(item) => ({
                  key: item._id,
                  value: item._id,
                  name: `${item.fullname} (${language
                    .formatMessage({
                      id: item.role.name,
                    })
                    .toLocaleLowerCase()})`,
                })}
              />
            </div>
            <Table
              showSorterTooltip={false}
              title={() => (
                <>
                  <strong>
                    <FormattedMessage id="expense_count" />:{' '}
                    {totals.total_clients.toLocaleString()}
                  </strong>{' '}
                  <br />{' '}
                  <strong>
                    <FormattedMessage id="total" />:{' '}
                    {formatNumber(totals.total_balance, '֏')}
                  </strong>{' '}
                </>
              )}
              bordered={true}
              columns={[
                {
                  title: language.formatMessage({ id: 'type' }),
                  dataIndex: 'type',
                  render: (type) => type.name,
                },
                {
                  title: language.formatMessage({ id: 'name' }),
                  dataIndex: 'user',
                  render: (user) =>
                    user ? (
                      <Link
                        to={{
                          pathname: `/view/user/${user._id}`,
                        }}
                      >
                        {user.fullname}
                      </Link>
                    ) : (
                      ''
                    ),
                },
                {
                  title: language.formatMessage({ id: 'from_name' }),
                  dataIndex: 'fromUser',
                  render: (user) =>
                    user ? (
                      <Link
                        to={{
                          pathname: `/view/user/${user._id}`,
                        }}
                      >
                        {user.fullname}
                      </Link>
                    ) : (
                      ''
                    ),
                },
                {
                  title: language.formatMessage({ id: 'price' }),
                  dataIndex: 'price',
                  render: (price) => formatNumber(price, '֏'),
                },
                {
                  title: language.formatMessage({ id: 'description' }),
                  dataIndex: 'description',
                  render: (text) => (
                    <div
                      style={{
                        height: 60,
                        overflowY: 'auto',
                      }}
                    >
                      {text}
                    </div>
                  ),
                  width: 300,
                },
                {
                  title: language.formatMessage({ id: 'expense_created_at' }),
                  dataIndex: 'created',
                  render: (text, record) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {getDate(text, true)}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {hasPermission('expense_edit') && (
                          <EditOutlined
                            style={{
                              color: '#1890ff',
                              cursor: 'pointer',
                              padding: 5,
                            }}
                            onClick={() => openEditModal(record)}
                          />
                        )}
                        {record.editHistory && record.editHistory.length > 0 && (
                          <HistoryOutlined
                            style={{
                              color: '#faad14',
                              cursor: 'pointer',
                              padding: 5,
                            }}
                            onClick={() => {
                              setHistoryData(record.editHistory)
                              setHistoryModalVisible(true)
                            }}
                          />
                        )}
                        {hasPermission('expense_archive') && (
                          <DeleteOutlined
                            style={{
                              color: 'red',
                              cursor: 'pointer',
                              padding: 5,
                            }}
                            onClick={() => archiveExpense(record._id)}
                          />
                        )}
                      </div>
                    </div>
                  ),
                },
              ]}
              pagination={{
                defaultPageSize: 9,
                total: expenses.length,
                position: ['bottomCenter'],
              }}
              dataSource={expenses}
              loading={loading}
            />
          </div>
        )}
      </div>
      <Modal
        title={language.formatMessage({ id: 'edit_expense' })}
        visible={editModalVisible}
        onOk={handleEditExpense}
        confirmLoading={editLoading}
        onCancel={() => {
          setEditModalVisible(false)
          setEditingExpense(null)
          editForm.resetFields()
        }}
        okText={language.formatMessage({ id: 'save' })}
        cancelText={language.formatMessage({ id: 'cancel' })}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="price"
            label={language.formatMessage({ id: 'price' })}
            rules={[
              {
                required: true,
                message: language.formatMessage({ id: 'expense_price_validation_empty' }),
              },
              {
                type: 'number',
                min: 1,
                message: language.formatMessage({ id: 'expense_price_validation_positive' }),
              },
            ]}
          >
            <InputNumber
              step="1000"
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => `${value}֏`}
              parser={(value) => value.replace('֏', '')}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={language.formatMessage({ id: 'description' })}
          >
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={language.formatMessage({ id: 'edit_history' })}
        visible={historyModalVisible}
        footer={null}
        onCancel={() => {
          setHistoryModalVisible(false)
          setHistoryData([])
        }}
      >
        {historyData.map((entry, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: 12,
              paddingBottom: 12,
              borderBottom: idx < historyData.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <div style={{ marginBottom: 4 }}>
              <strong>{entry.editedBy ? entry.editedBy.fullname : '—'}</strong>
            </div>
            <div style={{ color: '#999', marginBottom: 4 }}>
              {getDate(entry.editedAt, true)}
            </div>
            {entry.changes && entry.changes.price && entry.changes.price.old !== entry.changes.price.new && (
              <div style={{ color: '#666' }}>
                {language.formatMessage({ id: 'price' })}: {formatNumber(entry.changes.price.old, '֏')} → {formatNumber(entry.changes.price.new, '֏')}
              </div>
            )}
            {entry.changes && entry.changes.description && entry.changes.description.old !== entry.changes.description.new && (
              <div style={{ color: '#666' }}>
                {language.formatMessage({ id: 'description' })}: {entry.changes.description.old || '—'} → {entry.changes.description.new || '—'}
              </div>
            )}
          </div>
        ))}
      </Modal>
    </SContent>
  )
}
